import React, { useState, useEffect } from'react';
import { Link } from'react-router-dom';
import { motion } from'framer-motion';
import { Search, MapPin, Clock, Users, ChevronRight } from'lucide-react';
import { Input } from'../components/ui/input';
import { Badge } from'../components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from'../components/ui/avatar';

import { API_URL } from'../utils/api';

export default function InstructorsPage() {
 const [instructors, setInstructors] = useState([]);
 const [loading, setLoading] = useState(true);
 const [searchQuery, setSearchQuery] = useState('');

 useEffect(() => {
 fetchInstructors();
 }, []);

 const fetchInstructors = async () => {
 setLoading(true);
 try {
 const response = await fetch(`${API_URL}/api/instructors`);
 if (response.ok) {
 const data = await response.json();
 setInstructors(data);
 }
 } catch (error) {
 console.error('Error fetching instructors:', error);
 } finally {
 setLoading(false);
 }
 };

 const filteredInstructors = instructors.filter(instructor => {
 const query = searchQuery.toLowerCase();
 return (
 instructor.name.toLowerCase().includes(query) ||
 instructor.bio?.toLowerCase().includes(query) ||
 instructor.specialties?.some(s => s.toLowerCase().includes(query)) ||
 instructor.clubs?.some(c => c.name.toLowerCase().includes(query))
 );
 });

 if (loading) {
 return (
 <div className="min-h-screen bg-slate-50 flex items-center justify-center">
 <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-violet-500" />
 </div>
 );
 }

 return (
 <div className="min-h-screen bg-slate-50 transition-colors duration-200" data-testid="instructors-page">
 {/* Header */}
 <div className="bg-gradient-to-br from-violet-600 to-purple-700 py-16">
 <div className="container mx-auto px-6 lg:px-12">
 <motion.div
 initial={{ opacity: 0, y: 20 }}
 animate={{ opacity: 1, y: 0 }}
 className="text-center"
 >
 <h1 className="text-4xl md:text-5xl font-extrabold text-white mb-4" style={{ fontFamily:'Syne, sans-serif' }}>
 Line Dance Instructors
 </h1>
 <p className="text-violet-100 text-lg max-w-2xl mx-auto mb-8">
 Find experienced instructors teaching at clubs across the UK
 </p>
 
 {/* Search */}
 <div className="max-w-xl mx-auto relative">
 <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-300" />
 <Input
 placeholder="Search instructors, specialties, or clubs..."
 value={searchQuery}
 onChange={(e) => setSearchQuery(e.target.value)}
 className="pl-12 h-14 text-lg bg-white/95 backdrop-blur-sm border-0 shadow-xl"
 data-testid="instructor-search"
 />
 </div>
 </motion.div>
 </div>
 </div>

 {/* Results */}
 <div className="container mx-auto px-6 lg:px-12 py-12">
 <div className="flex items-center justify-between mb-8">
 <p className="text-slate-500">
 {filteredInstructors.length} instructor{filteredInstructors.length !== 1 ?'s' :''} found
 </p>
 </div>

 {filteredInstructors.length === 0 ? (
 <div className="text-center py-16">
 <Users className="w-16 h-16 text-slate-300 mx-auto mb-4" />
 <h2 className="text-xl font-semibold text-slate-700 mb-2">No instructors found</h2>
 <p className="text-slate-300">
 {searchQuery ?'Try adjusting your search' :'Be the first to register as an instructor!'}
 </p>
 </div>
 ) : (
 <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
 {filteredInstructors.map((instructor, index) => (
 <motion.div
 key={instructor.instructor_id}
 initial={{ opacity: 0, y: 20 }}
 animate={{ opacity: 1, y: 0 }}
 transition={{ delay: index * 0.05 }}
 >
 <Link to={`/instructors/${instructor.instructor_id}`} data-testid={`instructor-card-${instructor.instructor_id}`}>
 <div className="bg-white rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 overflow-hidden group">
 <div className="p-6">
 <div className="flex items-start gap-4">
 <Avatar className="w-16 h-16 border-2 border-violet-100">
 <AvatarImage src={instructor.image_url} alt={instructor.name} />
 <AvatarFallback className="bg-violet-100 text-violet-600 text-xl font-bold">
 {instructor.name.split('').map(n => n[0]).join('')}
 </AvatarFallback>
 </Avatar>
 <div className="flex-1 min-w-0">
 <h3 className="font-bold text-lg text-slate-900 group-hover:text-violet-600 transition-colors">
 {instructor.name}
 </h3>
 {instructor.years_experience && (
 <p className="text-sm text-violet-600 flex items-center mt-1">
 <Clock className="w-4 h-4 mr-1" />
 {instructor.years_experience} years experience
 </p>
 )}
 </div>
 <ChevronRight className="w-5 h-5 text-slate-300 group-hover:text-violet-500 transition-colors" />
 </div>

 <p className="text-slate-500 text-sm mt-4 line-clamp-2">
 {instructor.bio}
 </p>

 {/* Specialties */}
 {instructor.specialties?.length > 0 && (
 <div className="flex flex-wrap gap-1 mt-4">
 {instructor.specialties.slice(0, 3).map((specialty, i) => (
 <Badge key={i} variant="secondary" className="text-xs bg-violet-100 text-violet-700">
 {specialty}
 </Badge>
 ))}
 {instructor.specialties.length > 3 && (
 <Badge variant="secondary" className="text-xs bg-slate-100 text-slate-500">
 +{instructor.specialties.length - 3} more
 </Badge>
 )}
 </div>
 )}

 {/* Linked Clubs */}
 {instructor.clubs?.length > 0 && (
 <div className="mt-4 pt-4 border-t border-slate-100">
 <p className="text-xs text-slate-300 mb-2">Teaching at:</p>
 <div className="flex flex-wrap gap-2">
 {instructor.clubs.slice(0, 2).map((club, i) => (
 <div key={i} className="flex items-center gap-1 text-sm text-slate-700 bg-slate-50 px-2 py-1 rounded-lg">
 <MapPin className="w-3 h-3 text-violet-500" />
 {club.name}
 </div>
 ))}
 {instructor.clubs.length > 2 && (
 <span className="text-xs text-slate-300 px-2 py-1">
 +{instructor.clubs.length - 2} more
 </span>
 )}
 </div>
 </div>
 )}
 </div>
 </div>
 </Link>
 </motion.div>
 ))}
 </div>
 )}
 </div>
 </div>
 );
}
